import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
export interface CoworkingUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  imageUrl?: string | null;
  coffeeShopImage?: string | null;
  coffeeShopImageUpdatedAt?: string | null;
  /** Set while their camera tab is hidden: still here, shown "Away" (TX-5232). */
  coffeeShopAwayAt?: string | null;
  status?: string;
  username?: string;
  streakDays?: number;
  currentStreak?: number;
  [key: string]: unknown;
}
interface CoworkingUsersPage {
  users: CoworkingUser[];
  total: number;
  page: number;
  limit: number;
}

/** ~5s while coworking (webcam tiles); slower when only the dashboard badge needs a real count. */
const LIVE_COWORKING_POLL_MS = 5_000;
const BADGE_ONLY_POLL_MS = 90_000;
/**
 * Cadence while the socket is genuinely carrying coworking events.
 *
 * A reconcile, not the mechanism: presence and camera timestamps arrive over
 * `useCoworkFeed`, so this only has to catch whatever a dropped frame or a
 * missed gap left behind.
 *
 * It applies **only** when the socket is live. A member whose network eats the
 * WebSocket upgrade lives on the HTTP polling fallback permanently — that is
 * why the fallback exists — and slowing their roster to a minute would turn
 * coworking into a slideshow for exactly the people who cannot have the good
 * transport. See `docs/plans/coworking-presence-and-snapshots.md`.
 */
const SOCKET_RECONCILE_POLL_MS = 60_000;
export interface UseCoworkingUsersOptions {
  search?: string;
  enabled?: boolean;
  /**
   * From `useCoworkingStore` `isCoworkingActive`. Every subscriber using the same query key must pass the same value so refetch cadence stays consistent.
   * `false` → slower polling (dashboard badge). `true` → ~5s for live coworking / webcam UX.
   */
  isCoworkingActive: boolean;
  /**
   * Whether the socket is carrying coworking events right now.
   *
   * Drives the poll cadence. False — the default — keeps today's behaviour, so
   * a caller that has not been wired to the feed is never made worse.
   */
  isSocketLive?: boolean;
}

/**
 * Fetches coworking users (coffee shop / webcam roster) with infinite pagination.
 *
 * @param options - `isCoworkingActive` must match the store for all mounted observers sharing `["chat-users", search]`.
 */
export function useCoworkingUsers(options) {
  const $ = _c(19);
  const {
    search,
    enabled: t0,
    isCoworkingActive,
    isSocketLive: t1
  } = options;
  const enabled = t0 === undefined ? true : t0;
  const isSocketLive = t1 === undefined ? false : t1;
  const apiFetcher = useApiFetcher();
  const pollMs = !isCoworkingActive ? BADGE_ONLY_POLL_MS : isSocketLive ? SOCKET_RECONCILE_POLL_MS : LIVE_COWORKING_POLL_MS;
  const t2 = search ?? "";
  let t3;
  if ($[0] !== t2) {
    t3 = [...QUERY_KEYS.COWORKING_USERS, t2];
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== apiFetcher || $[3] !== search) {
    t4 = async t5 => {
      const {
        pageParam: t6
      } = t5;
      const pageParam = t6 === undefined ? 1 : t6;
      const params = new URLSearchParams({
        page: String(pageParam),
        limit: "20",
        status: "coffee"
      });
      if (search) {
        params.set("search", search);
      }
      return apiFetcher(`/api/v1/chat/users?${params.toString()}`);
    };
    $[2] = apiFetcher;
    $[3] = search;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isCoworkingActive && !isSocketLive ? 2000 : 60000;
  let t6;
  if ($[5] !== pollMs) {
    t6 = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return false;
      }
      return pollMs;
    };
    $[5] = pollMs;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== enabled || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      queryKey: t3,
      queryFn: t4,
      getNextPageParam: _temp,
      initialPageParam: 1,
      enabled,
      staleTime: t5,
      refetchInterval: t6,
      refetchIntervalInBackground: false,
      refetchOnMount: _temp2
    };
    $[7] = enabled;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const query_0 = useInfiniteQuery(t7);
  const count = query_0.data?.pages?.[0]?.total ?? 0;
  let t8;
  if ($[13] !== query_0.data?.pages) {
    t8 = query_0.data?.pages?.flatMap(_temp3) ?? [];
    $[13] = query_0.data?.pages;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const users = t8;
  let t9;
  if ($[15] !== count || $[16] !== query_0 || $[17] !== users) {
    t9 = {
      ...query_0,
      count,
      users
    };
    $[15] = count;
    $[16] = query_0;
    $[17] = users;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function _temp3(page) {
  return page.users ?? [];
}
function _temp2(query) {
  return query.isStale();
}
function _temp(lastPage) {
  const total = lastPage.total ?? 0;
  const limit = lastPage.limit ?? 20;
  const totalPages = Math.ceil(total / limit);
  const nextPage = (lastPage.page ?? 1) + 1;
  return nextPage <= totalPages ? nextPage : undefined;
}